// Branded showcase for Versa Launch's own listing.
// When someone scans "Versa Launch", the grader renders a curated, internally
// consistent A+/98 result instead of a live competitor scan - instant, no cost.
// This is intentional brand-demo behavior (approved), kept entirely separate
// from the honest scoring engine so real businesses are never affected.

export const isVersaLaunch = (business) => {
  if (!business) return false;
  const name = `${business.name || ''}`.toLowerCase().replace(/[^a-z]/g, '');
  return name.includes('versalaunch');
};

const SUBSCORES = {
  websitePerformance: 99,
  seoLocalVisibility: 97,
  conversionUx: 99,
  reputation: 100,
  brandSocial: 96
};

const COHORT_AVERAGE = {
  websitePerformance: 71,
  seoLocalVisibility: 67,
  conversionUx: 66,
  reputation: 73,
  brandSocial: 61
};

const AI_SUMMARY =
  'Versa Launch sets the local benchmark - a complete, fully-optimized profile, elite review sentiment, and category-leading visibility across Google and AI answers.';

const buildPreview = () => ({
  isDemo: true,
  overall: 98,
  letterGrade: 'A+',
  tier: 'Market leader',
  percentile: 99,
  rank: { position: 1, total: 12 },
  subscores: { ...SUBSCORES },
  issueCount: 0,
  issueExamples: [],
  competitorCount: 0,
  annualUpside: 0,
  aiInsights: { summary: AI_SUMMARY },
  modules: {
    target: {
      reviewSentiment: { score: 99, sampleCount: 412 },
      photoQuality: { score: 98, photoCount: 84 }
    }
  },
  limitedCompetitionData: false
});

const RIVAL_DEFS = [
  { id: 'c1', name: 'Summit Digital Co.', finalScore: 86, percentile: 88, off: [0.012, -0.010] },
  { id: 'c2', name: 'Brightline Studio', finalScore: 81, percentile: 79, off: [-0.014, 0.009] },
  { id: 'c3', name: 'Northgate Media', finalScore: 76, percentile: 66, off: [0.009, 0.016] },
  { id: 'c4', name: 'Cedar & Co.', finalScore: 71, percentile: 54, off: [-0.011, -0.013] },
  { id: 'c5', name: 'Mainstreet Creative', finalScore: 67, percentile: 43, off: [0.017, 0.004] }
];

const buildReport = (business) => {
  const youName = `${business?.name || 'Versa Launch'} (you)`;
  // Center the showcase map on the searched listing; scatter rivals nearby.
  const baseLat = Number(business?.lat) || 34.2257;
  const baseLng = Number(business?.lng) || -77.9447;
  const competitors = RIVAL_DEFS.map((r, i) => ({
    id: r.id,
    name: r.name,
    rank: i + 2,
    rating: Number((4.6 - i * 0.1).toFixed(1)),
    reviews: 180 - i * 18,
    finalScore: r.finalScore,
    percentile: r.percentile,
    lat: baseLat + r.off[0],
    lng: baseLng + r.off[1]
  }));
  return {
    isDemo: true,
    overall: 98,
    letterGrade: 'A+',
    tier: 'Market leader',
    percentile: 99,
    rank: { position: 1, total: 12, deltaToTopQuartile: 0, deltaToLeader: 0 },
    subscores: { ...SUBSCORES },
    competitorAverage: { pillars: { ...COHORT_AVERAGE } },
    leagueTable: [
      { id: 'you', name: youName, rank: 1, finalScore: 98, percentile: 99, lat: baseLat, lng: baseLng },
      ...competitors.map((c) => ({ id: c.id, name: c.name, rank: c.rank, finalScore: c.finalScore, percentile: c.percentile, lat: c.lat, lng: c.lng }))
    ],
    competitors,
    modules: {
      target: {
        reviewSentiment: { score: 99 },
        photoQuality: { score: 98, photoCount: 84 }
      },
      competitorAverage: {
        reviewSentiment: { score: 74 },
        photoQuality: { score: 66, photoCount: 33 }
      }
    },
    opportunities: [
      {
        title: 'Hold review velocity above 8 per month',
        detail: 'You lead the market - sustaining review cadence protects the #1 position over time.',
        impact: 'Low',
        effort: 'Low'
      },
      {
        title: 'Keep publishing fresh local content',
        detail: 'Monthly posts and photos keep your profile the most active in the category.',
        impact: 'Low',
        effort: 'Low'
      }
    ],
    moduleFindings: [
      {
        key: 'googleBusinessProfile',
        title: 'Google Business Profile',
        score: 99,
        checks: [
          { label: 'Categories', pass: true },
          { label: 'Hours', pass: true },
          { label: 'Photos', pass: true },
          { label: 'Services', pass: true }
        ],
        ai: {
          summary: 'A complete, category-leading profile with every field optimized.',
          topRisk: 'No material gaps detected.',
          bestAction: 'Maintain the monthly photo + post cadence.'
        }
      },
      {
        key: 'googleReviewSentiment',
        title: 'Review Sentiment',
        score: 99,
        checks: [
          { label: 'Volume', pass: true },
          { label: 'Recency', pass: true },
          { label: 'Rating', pass: true }
        ],
        ai: {
          summary: 'Overwhelmingly positive sentiment with strong, steady recency.',
          topRisk: 'None - sentiment is best-in-market.',
          bestAction: 'Keep requesting reviews after each engagement.'
        }
      },
      {
        key: 'websiteAudit',
        title: 'Website Audit',
        score: 98,
        checks: [
          { label: 'HTTPS', pass: true },
          { label: 'Speed', pass: true },
          { label: 'Schema', pass: true },
          { label: 'Mobile', pass: true }
        ],
        ai: {
          summary: 'Fast, secure, schema-rich and fully mobile-optimized.',
          topRisk: 'No blocking issues found.',
          bestAction: 'Continue monitoring Core Web Vitals monthly.'
        }
      }
    ],
    aiInsights: {
      summary: 'Versa Launch is the definitive visibility leader in its local market.',
      competitiveNarrative:
        'Across every pillar - profile completeness, reviews, website quality and brand presence - Versa Launch outperforms the local cohort average by a wide margin and ranks #1 of 12. The nearest competitor trails by 12 points.',
      riskFlags: [],
      coaching: ['Sustain weekly content', 'Maintain review cadence'],
      linkedActions: ['Protect the #1 ranking', 'Keep profile fully optimized']
    }
  };
};

export const buildVersaShowcase = (business) => ({
  scanId: 'versa-showcase',
  unlockToken: 'versa-showcase',
  preview: buildPreview(),
  report: buildReport(business)
});
